import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useTransition, animated } from '@react-spring/web';

import { useGame } from '../hooks/useGame';
import { useProductList, Product as ProductType } from '../hooks/useProducts';
import MainLayout from '../components/MainLayout/MainLayout';
import { getNumberOfPrizes } from '../constants/gameConfig';
import { isPersistenceEnabled } from '../store/index';
import { ApiManager } from '../services/ApiManager';
import { useSession } from '../hooks/useSession';
import { useSettingsRedux } from '../hooks/useSettingsRedux';
import { useLocaleMessaging } from '../hooks/useLocaleMessaging';
import { ProductFeedItem } from '../services/types/api';
import { getCurrentLanguage, getCurrentCountry } from '../constants/localization';
import Default from './Default';
import Game from './Game';
import Product from './Product';
import Rules from './Rules';
import SizeGuide from './SizeGuide';
import Reminder from './Reminder';

const FEED_LOADED_STORAGE_KEY = 'playmerce_feed_loaded';
const GAME_SCROLL_POSITION_KEY = 'playmerce_game_scroll_position';
const SESSION_STARTED_STORAGE_KEY = 'playmerce_session_started';
const SESSION_START_TIME_STORAGE_KEY = 'playmerce_session_start_time';

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const routesContainerRef = useRef<HTMLDivElement>(null);
  const previousPathnameRef = useRef<string>('');
  const [storedScrollPosition, setStoredScrollPosition] = useState<number>(0);

  // Define route path variables at the top for reuse
  const rootPath = process.env.REACT_APP_ROOT_PATH || '';
  const isGameRoute = location.pathname === `${rootPath}/game` || location.pathname === '/game';
  const isDefaultRoute = location.pathname === `${rootPath}/` || location.pathname === '/';

  const { sessionId, getFormattedExperiments } = useSession();

  // Initialize locale messaging for all routes to handle handshake
  useLocaleMessaging();

  // Auto-fetch settings for Game route and non-Default routes
  const shouldAutoFetchSettings = !isDefaultRoute || isGameRoute;
  const { settings, refetch: refetchSettings } = useSettingsRedux(shouldAutoFetchSettings);

  const [feedLoaded, setFeedLoaded] = useState<boolean>(() => {
    if (isPersistenceEnabled()) {
      const savedState = localStorage.getItem(FEED_LOADED_STORAGE_KEY);
      return savedState === 'true';
    }
    return false;
  });

  // Feed data state - this is now the primary data source
  const [feedProducts, setFeedProducts] = useState<ProductType[]>([]);
  const [loadingFeedProducts, setLoadingFeedProducts] = useState(false);
  const feedLoadAttempted = useRef(false);

  // Use only feed products - no static fallback
  const fetchedProducts = feedProducts;
  const loadingProducts = loadingFeedProducts;
  const productFetchError = null;

  const {
    items,
    loading: gameStateLoading,
    claimedPrizes,
    walletAmount,
    timeRemaining,
    showPrizeAnimation,
    showDiscountApplied,
    flyingPrizes,
    flyingDiscounts,
    walletPulse,
    walletRef,
    selectedProduct,
    appState,
    setAppState,
    handlePrizeClick,
    handleProductClick,
  } = useGame({
    fetchedProducts,
    loadingProducts,
    productFetchError,
    timeLimitInSeconds: settings?.timeLimitInSeconds,
    onTimerComplete: () => setAppState('dismissed'),
  });

  // Helper function to send session start analytics once
  const sendSessionStartAnalytics = useCallback(async () => {
    if (!sessionId) return;

    // Check if session start has already been sent
    const sessionStarted = sessionStorage.getItem(SESSION_STARTED_STORAGE_KEY);
    if (sessionStarted === 'true') return;

    try {
      await ApiManager.analytics.playmerce_session_start(
        {
          page: 'game',
        },
        sessionId
      );

      // Mark session as started and store start timestamp
      sessionStorage.setItem(SESSION_STARTED_STORAGE_KEY, 'true');
      sessionStorage.setItem(SESSION_START_TIME_STORAGE_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to send session start analytics:', error);
    }
  }, [sessionId]);

  // Transform feed items to Product format
  const transformFeedToProducts = useCallback((feedItems: ProductFeedItem[]): ProductType[] => {
    return feedItems.map((item) => ({
      id: item.id.toString(),
      title: item.title,
      handle: item.title.toLowerCase().replace(/\s+/g, '-'), // Generate handle from title
      description: item.descriptionHtml?.replace(/<[^>]*>/g, '') || '', // Strip HTML
      descriptionHtml: item.descriptionHtml || '',
      vendor: undefined, // Feed doesn't include vendor information
      options: [], // Feed doesn't include options, will be empty
      media: {
        edges: item.featuredImage
          ? [
              {
                node: {
                  id: 'featured-image',
                  image: {
                    url: item.featuredImage.url,
                  },
                },
              },
            ]
          : [],
      },
      variants: {
        edges: item.primaryVariant
          ? [
              {
                node: {
                  id: item.primaryVariant.id.toString(),
                  title: item.primaryVariant.title,
                  price: (item.primaryVariant.price.amountInCents / 100).toString(),
                  sku: '',
                  position: 1,
                  availableForSale: item.primaryVariant.availableForSale,
                  inventoryQuantity: item.primaryVariant.availableForSale ? 10 : 0,
                },
              },
            ]
          : [],
      },
      other_colors: null,
      metafields: {
        edges: [],
      },
      priceRange: {
        minVariantPrice: {
          amount: item.primaryVariant ? (item.primaryVariant.price.amountInCents / 100).toString() : '0',
          currencyCode: item.primaryVariant?.price.currencyCode || 'EUR',
        },
      },
      featuredImage: item.featuredImage
        ? {
            url: item.featuredImage.url,
            altText: item.featuredImage.altText || item.title,
          }
        : null,
    }));
  }, []);

  const handleLoadFeed = useCallback(
    async (departmentId?: number) => {
      try {
        setLoadingFeedProducts(true);

        // Use provided department ID or get from settings
        const targetDepartmentId = departmentId || settings?.departments?.[0]?.id || 1;

        // Fetch feed from remote API
        const response = await ApiManager.customer.getScrollCollectFeed(
          getCurrentCountry(), // country
          getCurrentLanguage(), // language
          targetDepartmentId, // department ID from settings or parameter
          getFormattedExperiments(), // experiments with bar_title
          sessionId || undefined // session ID
        );

        // Transform feed data to Product format
        const transformedProducts = transformFeedToProducts(response.data);
        setFeedProducts(transformedProducts);

        // Clear any cached game state to force regeneration with feed products
        if (isPersistenceEnabled()) {
          localStorage.removeItem('playmerce_product_order');
          localStorage.removeItem('playmerce_game_state');
        }

        setFeedLoaded(true);
        if (isPersistenceEnabled()) {
          localStorage.setItem(FEED_LOADED_STORAGE_KEY, 'true');
        }

        // Send session start analytics when feed is successfully loaded
        await sendSessionStartAnalytics();
      } catch (error) {
        console.error('Failed to load feed from API:', error);
        // Still set feedLoaded to true to prevent blocking the UI
        setFeedLoaded(true);
        if (isPersistenceEnabled()) {
          localStorage.setItem(FEED_LOADED_STORAGE_KEY, 'true');
        }
      } finally {
        setLoadingFeedProducts(false);
      }
    },
    [settings?.departments, sessionId, transformFeedToProducts, sendSessionStartAnalytics]
  );

  // Auto-load feed data only when on Game route
  useEffect(() => {
    const shouldLoad =
      isGameRoute &&
      !feedLoadAttempted.current &&
      !loadingFeedProducts &&
      feedProducts.length === 0 &&
      settings?.departments?.[0]?.id &&
      sessionId;

    if (shouldLoad) {
      feedLoadAttempted.current = true;

      const loadFeed = async () => {
        try {
          setLoadingFeedProducts(true);

          const departmentId = settings.departments[0].id;
          const response = await ApiManager.customer.getScrollCollectFeed(
            getCurrentCountry(),
            getCurrentLanguage(),
            departmentId,
            getFormattedExperiments(),
            sessionId
          );

          const transformedProducts = transformFeedToProducts(response.data);
          setFeedProducts(transformedProducts);

          setFeedLoaded(true);
          if (isPersistenceEnabled()) {
            localStorage.setItem(FEED_LOADED_STORAGE_KEY, 'true');
          }

          // Send session start analytics when feed is successfully loaded
          await sendSessionStartAnalytics();
        } catch (error) {
          console.error('Failed to load feed from API:', error);
          setFeedLoaded(true);
          if (isPersistenceEnabled()) {
            localStorage.setItem(FEED_LOADED_STORAGE_KEY, 'true');
          }
        } finally {
          setLoadingFeedProducts(false);
        }
      };

      loadFeed();
    }
  }, [
    isGameRoute,
    settings?.departments?.[0]?.id,
    sessionId,
    feedProducts.length,
    loadingFeedProducts,
    transformFeedToProducts,
    sendSessionStartAnalytics,
  ]);

  // Common StatusBar props that will be passed to MainLayout
  const statusBarProps = {
    walletAmount,
    claimedPrizes,
    totalPrizes: getNumberOfPrizes(settings),
    showPrizeAnimation,
    walletPulse,
    walletRef,
    selectedProduct,
    showDiscountApplied,
    timeRemaining,
    onLoadFeed: handleLoadFeed,
    feedLoaded,
    appState,
    setAppState,
    itemCount: items.length, // Add item count for analytics
  };

  // Determine transition direction based on route
  const isProductRoute = location.pathname.includes('/product/');
  const isRulesRoute = location.pathname.includes('/rules');
  const isSizeGuideRoute = location.pathname.includes('/size-guide');
  const isReminderTransition = location.pathname.includes('/reminder');
  const direction = isProductRoute || isRulesRoute || isSizeGuideRoute || isReminderTransition ? 'forward' : 'backward';

  // Load stored scroll position on mount
  useEffect(() => {
    if (isPersistenceEnabled()) {
      const savedScrollPosition = localStorage.getItem(GAME_SCROLL_POSITION_KEY);
      if (savedScrollPosition) {
        setStoredScrollPosition(parseInt(savedScrollPosition, 10));
      }
    }
  }, []);

  // No need to handle titlebar expansion since app is always expanded by default

  // Handle scroll position management when location changes
  useEffect(() => {
    const container = routesContainerRef.current;
    if (!container) return;

    const currentPathname = location.pathname;
    const previousPathname = previousPathnameRef.current;

    // Skip if this is the first render or pathname hasn't actually changed
    if (!previousPathname || currentPathname === previousPathname) {
      previousPathnameRef.current = currentPathname;
      return;
    }

    const currentIsProductRoute = currentPathname.includes('/product/');
    const currentIsRulesRoute = currentPathname.includes('/rules');
    const currentIsSizeGuideRoute = currentPathname.includes('/size-guide');
    const currentIsGameRoute = currentPathname === `${rootPath}/game` || currentPathname === '/game';
    const previousIsProductRoute = previousPathname.includes('/product/');
    const previousIsRulesRoute = previousPathname.includes('/rules');
    const previousIsSizeGuideRoute = previousPathname.includes('/size-guide');
    const previousIsGameRoute = previousPathname === `${rootPath}/game` || previousPathname === '/game';

    // Navigating FROM game TO product page, rules page, or size guide page
    if (previousIsGameRoute && (currentIsProductRoute || currentIsRulesRoute || currentIsSizeGuideRoute)) {
      // Find the game-content div within the main-layout-content
      const mainLayoutContent = document.querySelector('.main-layout-content');
      const gameContentElement = mainLayoutContent ? mainLayoutContent.querySelector('.game-content') : null;
      const currentScrollTop = gameContentElement
        ? gameContentElement.scrollTop
        : mainLayoutContent
        ? mainLayoutContent.scrollTop
        : 0;
      setStoredScrollPosition(currentScrollTop);
      if (isPersistenceEnabled()) {
        localStorage.setItem(GAME_SCROLL_POSITION_KEY, currentScrollTop.toString());
      }

      // Reset scroll to top for product/rules page
      setTimeout(() => {
        const resetElement = mainLayoutContent || container;
        if (resetElement) {
          resetElement.scrollTop = 0;
        }
      }, 50); // Small delay to ensure transition has started
    }
    // Navigating FROM product, rules, or size guide TO game page
    else if ((previousIsProductRoute || previousIsRulesRoute || previousIsSizeGuideRoute) && currentIsGameRoute) {
      // Restore scroll position after transition with smooth animation
      setTimeout(() => {
        // Find the game-content div within the main-layout-content and restore its scroll position
        const mainLayoutContent = document.querySelector('.main-layout-content');
        const gameContentElement = mainLayoutContent ? mainLayoutContent.querySelector('.game-content') : null;
        if (gameContentElement) {
          gameContentElement.scrollTo({
            top: storedScrollPosition,
            behavior: 'auto',
          });
        } else if (mainLayoutContent) {
          // Fallback to main layout content scroll if game-content not found
          mainLayoutContent.scrollTo({
            top: storedScrollPosition,
            behavior: 'auto',
          });
        }
      }, 50); // Wait for transition to complete
    }

    // Update previous pathname
    previousPathnameRef.current = currentPathname;
  }, [location.pathname, rootPath, storedScrollPosition]);

  // Configure transitions for the current location
  const transitions = useTransition(location, {
    keys: (location) => location.pathname,
    from: {
      transform: direction === 'forward' ? 'translateX(100%)' : 'translateX(-100%)',
      opacity: 1,
    },
    enter: {
      transform: 'translateX(0%)',
      opacity: 1,
    },
    leave: {
      transform: direction === 'forward' ? 'translateX(-100%)' : 'translateX(100%)',
      opacity: 1,
    },
    config: {
      tension: 280,
      friction: 30,
    },
  });

  // Check if current route should not show MainLayout
  const isReminderRoute = location.pathname === `${rootPath}/reminder` || location.pathname === '/reminder';

  if (isDefaultRoute) {
    // Render default route without MainLayout
    return <Default />;
  }

  if (isReminderRoute) {
    // Render reminder route without MainLayout
    return <Reminder />;
  }

  return (
    <MainLayout statusBarProps={statusBarProps} rootPath={rootPath} location={location.pathname}>
      {transitions((style, location) => (
        <animated.div
          ref={routesContainerRef}
          style={{
            ...style,
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            overflow: 'hidden',
          }}
        >
          <Routes location={location}>
            <Route
              path={`${rootPath}/game`}
              element={
                <Game
                  feedLoaded={feedLoaded}
                  setFeedLoaded={setFeedLoaded}
                  items={items}
                  selectedProduct={selectedProduct}
                  handlePrizeClick={handlePrizeClick}
                  handleProductClick={handleProductClick}
                  flyingPrizes={flyingPrizes}
                  flyingDiscounts={flyingDiscounts}
                  walletRef={walletRef}
                  onLoadFeed={handleLoadFeed}
                  loadingFeedProducts={loadingFeedProducts}
                />
              }
            />
            <Route path={`${rootPath}/product/:productId`} element={<Product />} />
            <Route path={`${rootPath}/rules`} element={<Rules />} />
            <Route path={`${rootPath}/size-guide`} element={<SizeGuide />} />
          </Routes>
        </animated.div>
      ))}
    </MainLayout>
  );
};

export default AppRoutes;
